import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { ApplicantFormGroup } from '../../../../shared/form-groups/applicant.form-group';

@Component({
    selector: 'app-applicant-form',
    templateUrl: './applicant-form.component.html',
    styleUrl: './applicant-form.component.scss',
    standalone: false
})
export class ApplicantFormComponent implements OnInit, OnDestroy {
    @Input({ required: true }) applicantFormGroup!: ApplicantFormGroup;
    /** When true, email is readonly (e.g. primary applicant from profile, or co-applicant after save). */
    @Input() readonlyEmail = false;
    /** When provided, shows a tooltip by the email field (e.g. when email is readonly after save). */
    @Input() emailTooltipText?: string;
    /** Distinct `name`/`id` for the checkbox when multiple applicant forms could exist in the DOM. */
    @Input() employerSameAsHomeGroupName = 'ApplicantEmployerSameAsHome';

    private checkboxSub?: Subscription;
    private syncSub?: Subscription;

    protected states = [
        "UT", "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
        "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
        "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
        "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
        "SD", "TN", "TX", "VT", "VA", "WA", "WV", "WI", "WY"
    ];

    constructor() { }

    ngOnInit(): void {
        const esc = this.employerSameAsHomeControl;
        this.checkboxSub = esc.valueChanges.subscribe((checked) => this.syncEmployerSameAsHomeUi(checked));
        this.syncEmployerSameAsHomeUi(esc.value);
    }

    /** Bound to the applicant form group; persisted as `employer_same_as_home`. */
    get employerSameAsHomeControl(): FormControl<boolean> {
        return this.applicantFormGroup.get('employerSameAsHome') as FormControl<boolean>;
    }

    private syncEmployerSameAsHomeUi(checked: boolean): void {
        if (checked) {
            this.applyHomeToEmployer();
            this.syncSub?.unsubscribe();
            const g = this.applicantFormGroup;
            this.syncSub = merge(
                g.get('address')!.valueChanges,
                g.get('city')!.valueChanges,
                g.get('state')!.valueChanges,
                g.get('zip')!.valueChanges,
                g.get('phone')!.valueChanges,
            ).subscribe(() => this.applyHomeToEmployer());
            this.setEmployerAddressFieldsDisabled(true);
        } else {
            this.syncSub?.unsubscribe();
            this.syncSub = undefined;
            this.setEmployerAddressFieldsDisabled(false);
        }
    }

    ngOnDestroy(): void {
        this.checkboxSub?.unsubscribe();
        this.syncSub?.unsubscribe();
    }

    get showBankruptcy() {
        return this.applicantFormGroup.get('bankruptcy')?.value === 'Yes';
    }

    private applyHomeToEmployer(): void {
        const g = this.applicantFormGroup;
        const patch = {
            employerAddress: g.get('address')!.value ?? '',
            employerCity: g.get('city')!.value ?? '',
            employerState: g.get('state')!.value ?? '',
            employerZip: g.get('zip')!.value ?? '',
            employerPhone: g.get('phone')!.value ?? '',
        };
        g.patchValue(patch, { emitEvent: false });
    }

    private setEmployerAddressFieldsDisabled(disabled: boolean): void {
        const keys = ['employerAddress', 'employerCity', 'employerState', 'employerZip', 'employerPhone'] as const;
        for (const k of keys) {
            const c = this.applicantFormGroup.get(k)!;
            if (disabled) {
                c.disable({ emitEvent: false });
            } else {
                c.enable({ emitEvent: false });
            }
        }
    }
}
